import * as React from 'react';
import { cn } from '../../utils/cn';

type ProseSize = 'sm' | 'base' | 'lg' | 'xl';
type ProseMaxWidth = 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export interface ProseProps {
  children: React.ReactNode;
  size?: ProseSize;
  maxWidth?: ProseMaxWidth;
  centered?: boolean;
  serif?: boolean;
  dropCap?: boolean;
  balancedText?: boolean;
  className?: string;
}

const sizeClasses: Record<ProseSize, string> = {
  sm: 'prose-sm',
  base: 'prose',
  lg: 'prose-lg',
  xl: 'prose-xl',
};

const maxWidthClasses: Record<ProseMaxWidth, string> = {
  none: 'max-w-none',
  sm: 'max-w-prose-sm',
  md: 'max-w-prose',
  lg: 'max-w-prose-lg',
  xl: 'max-w-prose-xl',
  '2xl': 'max-w-prose-2xl',
};

export const Prose = React.forwardRef<HTMLDivElement, ProseProps>(
  (
    {
      children,
      size = 'base',
      maxWidth = 'lg',
      centered = false,
      serif = false,
      dropCap = false,
      balancedText = false,
      className,
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'prose',
          sizeClasses[size],
          maxWidthClasses[maxWidth],
          centered && 'mx-auto',
          serif && 'font-serif',
          balancedText && '[text-wrap:balance]',
          // Dark mode support
          'prose-headings:text-base-content',
          'prose-p:text-base-content',
          'prose-strong:text-base-content',
          'prose-em:text-base-content',
          'prose-blockquote:text-base-content/80',
          'prose-lead:text-base-content',
          'prose-a:text-primary',
          'prose-a:no-underline',
          'hover:prose-a:underline',
          'prose-code:text-base-content',
          'prose-code:bg-base-200',
          'prose-code:px-1',
          'prose-code:py-0.5',
          'prose-code:rounded',
          'prose-pre:bg-base-200',
          'prose-pre:text-base-content',
          'prose-ol:text-base-content',
          'prose-ul:text-base-content',
          'prose-li:text-base-content',
          'prose-table:text-base-content',
          'prose-thead:border-base-300',
          'prose-tr:border-base-300',
          'prose-th:text-base-content',
          'prose-td:text-base-content',
          'prose-hr:border-base-300',
          'prose-figure:text-base-content',
          'prose-figcaption:text-base-content/70',
          'prose-video:rounded',
          'prose-img:rounded',
          // First letter drop cap
          dropCap &&
            'prose-p:first-of-type:first-letter:text-7xl prose-p:first-of-type:first-letter:font-bold prose-p:first-of-type:first-letter:float-left prose-p:first-of-type:first-letter:mr-3 prose-p:first-of-type:first-letter:mt-1 prose-p:first-of-type:first-letter:text-primary',
          className
        )}
      >
        {children}
      </div>
    );
  }
);

Prose.displayName = 'Prose';
